// Jun-2026 — Phase 2B: PR-versus-PO comparison viewer (read-only).
// Computed server-side from the IMMUTABLE approved PR (the frozen baseline)
// joined to the live Draft PO by stable line references. Commercial PR values
// respect pr_commercials; PO values follow the existing PO visibility model.
import { useEffect, useState } from "react";
import { api, formatINR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { X, GitCompareArrows, History } from "lucide-react";

const STATE_STYLE = {
  Unchanged: "bg-slate-100 text-slate-600 border-slate-300",
  Modified: "bg-amber-100 text-amber-800 border-amber-300",
  Added: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Removed: "bg-rose-100 text-rose-800 border-rose-300",
};

const Chip = ({ s }) => (
  <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border rounded ${STATE_STYLE[s] || STATE_STYLE.Unchanged}`}>{s}</span>
);

const diffCls = (fields, f) => (fields.includes(f) ? "bg-amber-50 font-bold text-amber-900" : "");

export default function ProjectPoComparison({ projectId, prId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/projects/${projectId}/prs/${prId}/po-comparison`)
      .then(r => setData(r.data))
      .catch(e => { setErr(e?.response?.data?.detail || "Comparison unavailable"); toast.error(e?.response?.data?.detail || "Comparison unavailable"); });
  }, [projectId, prId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-start justify-center p-4 overflow-y-auto" data-testid="pr-po-comparison-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl my-6">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg">
          <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            <GitCompareArrows size={16} className="text-[#F97316]" /> PR ↔ Draft PO Comparison
          </div>
          <button data-testid="comparison-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {err && <div className="p-6 text-sm text-rose-700" data-testid="comparison-error">{err}</div>}
        {!err && !data && <div className="p-6 text-sm text-slate-500">Loading…</div>}
        {data && (
          <div className="p-5 space-y-4 text-xs">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-slate-200 rounded p-3" data-testid="comparison-pr-header">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Original PR (immutable baseline)</div>
                <div className="font-mono font-bold">{data.pr.pr_number}</div>
                <div>Vendor: <b>{data.pr.vendor_code ? `${data.pr.vendor_code} · ` : ""}{data.pr.vendor_name}</b></div>
                <div>Required by: {data.pr.required_by_date ? formatDate(data.pr.required_by_date) : "—"} · Delivery: {data.pr.delivery_location || "—"}</div>
                {data.commercials_visible && <div>Estimated total: <b className="tabular-nums">{formatINR(data.values.pr_est_total || 0)}</b></div>}
              </div>
              <div className="border border-slate-200 rounded p-3" data-testid="comparison-po-header">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Current Draft PO</div>
                <div className="font-mono font-bold">{data.po.po_number} <span className="text-slate-500">({data.po.status})</span></div>
                <div className={data.vendor_changed ? "bg-amber-50 font-bold text-amber-900 inline-block px-1 rounded" : ""} data-testid="comparison-po-vendor">
                  Vendor: <b>{data.po.vendor_code ? `${data.po.vendor_code} · ` : ""}{data.po.vendor_name}</b>{data.vendor_changed ? " (changed)" : ""}
                </div>
                <div>Expected delivery: {data.po.expected_delivery_date ? formatDate(data.po.expected_delivery_date) : "—"} · Ship to: {data.po.shipping_address || "—"}</div>
                <div>PO total: <b className="tabular-nums">{formatINR(data.values.po_total_amount || 0)}</b>
                  {data.commercials_visible && <span className="text-slate-500"> · difference vs PR estimate: <b className="tabular-nums">{formatINR(data.values.difference || 0)}</b></span>}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Converted by {data.po.converted_by} · {formatDate(data.po.converted_at)}</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-[11px]" data-testid="comparison-lines-table">
                <thead>
                  <tr className="text-left text-[9px] uppercase tracking-wider text-slate-600 bg-slate-100">
                    <th className="px-2 py-1.5">State</th>
                    <th className="px-2 py-1.5">PR Line</th>
                    <th className="px-2 py-1.5">Item (PR → PO)</th>
                    <th className="px-2 py-1.5">Classification</th>
                    <th className="px-2 py-1.5">UOM</th>
                    <th className="px-2 py-1.5 text-right">Qty (PR → PO)</th>
                    <th className="px-2 py-1.5 text-right">Rate (PR → PO)</th>
                    <th className="px-2 py-1.5 text-right">Tax % (PR → PO)</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lines || []).map((ln, i) => {
                    const cf = ln.changed_fields || [];
                    return (
                      <tr key={i} className="border-t border-slate-100" data-testid={`comparison-line-${i}`}>
                        <td className="px-2 py-1"><Chip s={ln.state} /></td>
                        <td className="px-2 py-1 tabular-nums">{ln.pr?.line_no ?? "—"}</td>
                        <td className={`px-2 py-1 max-w-[220px] ${diffCls(cf, "item")}`}>
                          {ln.pr ? <><span className="font-mono text-[10px]">{ln.pr.item_code}</span> {ln.pr.item_name}</> : <span className="text-slate-400">—</span>}
                          {ln.po && (!ln.pr || cf.includes("item")) && <> → <span className="font-mono text-[10px]">{ln.po.item_code}</span> {ln.po.description}</>}
                        </td>
                        <td className={`px-2 py-1 ${diffCls(cf, "classification")}`}>
                          {ln.pr?.item_type || "—"}{ln.po && cf.includes("classification") ? ` → ${ln.po.item_type}` : (!ln.pr && ln.po ? ln.po.item_type : "")}
                        </td>
                        <td className={`px-2 py-1 ${diffCls(cf, "uom")}`}>
                          {ln.pr?.uom || ln.po?.uom || "—"}{cf.includes("uom") ? ` → ${ln.po?.uom}` : ""}
                        </td>
                        <td className={`px-2 py-1 text-right tabular-nums ${diffCls(cf, "quantity")}`}>
                          {ln.pr ? ln.pr.quantity : "—"}{ln.po && (!ln.pr || cf.includes("quantity")) ? ` → ${ln.po.quantity}` : ""}
                        </td>
                        <td className={`px-2 py-1 text-right tabular-nums ${diffCls(cf, "rate")}`}>
                          {ln.pr ? (data.commercials_visible ? (ln.pr.estimated_rate ?? "—") : "•••") : "—"}
                          {ln.po ? ` → ${ln.po.rate}` : ""}
                        </td>
                        <td className={`px-2 py-1 text-right tabular-nums ${diffCls(cf, "tax")}`}>
                          {ln.pr ? (data.commercials_visible ? (ln.pr.tax_percent ?? "—") : "•••") : "—"}
                          {ln.po ? ` → ${ln.po.gst_rate}` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(data.conversion_history || []).length > 0 && (
              <div data-testid="comparison-history">
                <div className="font-bold text-slate-700 mb-1 inline-flex items-center gap-1"><History size={12} /> Conversion History</div>
                {data.conversion_history.map((h, i) => (
                  <div key={i} className="text-[11px] text-slate-600 border-t border-slate-100 py-0.5">
                    <span className="font-mono">{h.po_number}</span> · converted {formatDate(h.converted_at)} by {h.converted_by} ·
                    <span className="text-rose-700"> cancelled {formatDate(h.cancelled_at)} by {h.cancelled_by} — {h.reason}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-1.5" data-testid="comparison-grn-note">
              {data.grn_note}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
